import { useMemo, useState } from 'react'
import {Download, Receipt, Sparkles} from 'lucide-react'
import { generateQuotePdf } from '@/lib/generateQuotePdf'

const TIERS = [
  { id: 'junior', label: 'Junior Freelancer', rate: 35 },
  { id: 'mid', label: 'Mid-Weight Designer', rate: 75 },
  { id: 'senior', label: 'Senior Art Director', rate: 140 },
  { id: 'boutique', label: 'Boutique Agency', rate: 220 },
  { id: 'enterprise', label: 'Enterprise Studio', rate: 380 },
] as const

const MARKETS = [
  { id: 't1', label: 'Tier 1 (US/UK/EU)', mult: 1 },
  { id: 't2', label: 'Tier 2 (LATAM/EE)', mult: 0.72 },
  { id: 't3', label: 'Tier 3 (Emerging)', mult: 0.5 },
] as const

const LICENSES = [
  { id: 'personal', label: 'Personal use only', addon: 0 },
  { id: 'smb', label: 'Small Business Commercial', addon: 0.18 },
  { id: 'worldwide', label: 'Worldwide Registered Trademark', addon: 0.45 },
] as const

function currency(n: number) {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
}

function PricingEstimator() {
  const [tier, setTier] = useState<(typeof TIERS)[number]['id']>('mid')
  const [market, setMarket] = useState<(typeof MARKETS)[number]['id']>('t1')
  const [license, setLicense] = useState<(typeof LICENSES)[number]['id']>('smb')
  const [hours, setHours] = useState(20)
  const [revisions, setRevisions] = useState(3)

  const quote = useMemo(() => {
    const t = TIERS.find((x) => x.id === tier)!
    const m = MARKETS.find((x) => x.id === market)!
    const l = LICENSES.find((x) => x.id === license)!

    const baseLabor = t.rate * hours * m.mult
    const revisionsPremium = revisions * (t.rate * 1.5) * m.mult
    const licenseAddon = (baseLabor + revisionsPremium) * l.addon
    const total = baseLabor + revisionsPremium + licenseAddon

    return {
      baseLabor,
      revisionsPremium,
      licenseAddon,
      total,
      license: l.label,
      tierLabel: t.label,
      hourlyRate: t.rate,
      marketLabel: m.label,
    }
  }, [tier, market, license, hours, revisions])

  const handleExportPdf = () => {
    generateQuotePdf({
      tierLabel: quote.tierLabel,
      hourlyRate: quote.hourlyRate,
      hours,
      revisions,
      marketLabel: quote.marketLabel,
      licenseLabel: quote.license,
      baseLabor: quote.baseLabor,
      revisionsPremium: quote.revisionsPremium,
      licenseAddon: quote.licenseAddon,
      total: quote.total,
    })
  }

  return (
    <div className="grid gap-6 lg:grid-cols-5">
      {/* Controls */}
      <div className="rounded-2xl border border-white/8 bg-[#0b1020] p-6 shadow-xl shadow-black/40 lg:col-span-3">
        <div className="mb-6">
          <label className="mb-2 block text-sm font-medium text-slate-300">Expertise Tier</label>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {TIERS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTier(t.id)}
                aria-pressed={tier === t.id}
                className={`rounded-xl border px-3 py-2.5 text-left text-xs font-medium transition-all ${
                  tier === t.id
                    ? 'border-indigo-400 bg-indigo-500/15 text-white shadow-lg shadow-indigo-500/20'
                    : 'border-white/8 bg-white/[0.02] text-slate-300 hover:border-white/20'
                }`}
              >
                {t.label}
                <span className="mt-0.5 block font-mono text-[11px] text-emerald-400">
                  ${t.rate}/hr
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <div className="mb-2 flex items-center justify-between">
            <label htmlFor="hours" className="text-sm font-medium text-slate-300">
              Expected Labor Hours
            </label>
            <span className="font-mono text-sm font-semibold text-indigo-300">{hours} hrs</span>
          </div>
          <input
            id="hours"
            type="range"
            min={5}
            max={80}
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            className="w-full"
            aria-valuemin={5}
            aria-valuemax={80}
            aria-valuenow={hours}
          />
        </div>

        <div className="mb-6">
          <div className="mb-2 flex items-center justify-between">
            <label htmlFor="revisions" className="text-sm font-medium text-slate-300">
              Revision Rounds
            </label>
            <span className="font-mono text-sm font-semibold text-indigo-300">{revisions}</span>
          </div>
          <input
            id="revisions"
            type="range"
            min={0}
            max={12}
            value={revisions}
            onChange={(e) => setRevisions(Number(e.target.value))}
            className="w-full"
            aria-valuemin={0}
            aria-valuemax={12}
            aria-valuenow={revisions}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="market" className="mb-2 block text-sm font-medium text-slate-300">
              Geographic Market Index
            </label>
            <select
              id="market"
              value={market}
              onChange={(e) => setMarket(e.target.value as typeof market)}
              className="w-full rounded-xl border border-white/8 bg-[#070a13] px-3 py-2.5 text-sm text-white focus:border-indigo-400 focus:outline-none"
            >
              {MARKETS.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="license" className="mb-2 block text-sm font-medium text-slate-300">
              Licensing Rights
            </label>
            <select
              id="license"
              value={license}
              onChange={(e) => setLicense(e.target.value as typeof license)}
              className="w-full rounded-xl border border-white/8 bg-[#070a13] px-3 py-2.5 text-sm text-white focus:border-indigo-400 focus:outline-none"
            >
              {LICENSES.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Receipt */}
      <div className="lg:col-span-2">
        <div className="relative overflow-hidden rounded-2xl border border-emerald-400/20 bg-gradient-to-b from-[#0c1322] to-[#0a0f1c] p-6 shadow-xl shadow-emerald-500/5">
          <div className="mb-5 flex items-center gap-2 border-b border-dashed border-white/10 pb-4">
            <Receipt className="h-5 w-5 text-emerald-400" />
            <span className="font-display text-lg font-semibold text-white">Itemized Quote</span>
          </div>

          <dl className="space-y-3 font-mono text-sm">
            <div className="flex items-center justify-between">
              <dt className="text-slate-400">Base Labor Fee</dt>
              <dd className="text-white">{currency(quote.baseLabor)}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-slate-400">Revisions Premium</dt>
              <dd className="text-white">{currency(quote.revisionsPremium)}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-slate-400">Licensing Add-on</dt>
              <dd className="text-white">{currency(quote.licenseAddon)}</dd>
            </div>
          </dl>

          <div className="mt-5 border-t border-dashed border-white/10 pt-4">
            <div className="flex items-end justify-between">
              <span className="text-sm font-medium text-slate-300">Total Estimate</span>
              <span className="font-mono text-3xl font-bold text-emerald-400">
                {currency(quote.total)}
              </span>
            </div>
            <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
              <Sparkles className="h-3.5 w-3.5 text-indigo-400" />
              {quote.license} included
            </p>
          </div>

          <button
            type="button"
            onClick={handleExportPdf}
            aria-label="Export itemized logo pricing quote as PDF"
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-3 text-sm font-semibold text-emerald-300 transition-all hover:scale-[1.02] hover:bg-emerald-500/20 hover:shadow-lg hover:shadow-emerald-500/20 focus:outline-none focus:ring-2 focus:ring-emerald-400/60"
          >
            <Download className="h-4 w-4" />
            Export Quote as PDF
          </button>
        </div>
      </div>
    </div>
  )
}

export default PricingEstimator
